import type { Dictionary } from './types'

const en: Dictionary = {
  common: {
    appName: 'Pivot',
    signIn: 'Sign in with Google',
    signOut: 'Sign out',
    loading: 'Loading…',
    save: 'Save',
    cancel: 'Cancel',
    retry: 'Retry',
    error: 'Something went wrong.',
    reload: 'Reload the page',
  },
  landing: {
    metaDescription:
      "Pivot reads every job on Belgium's niche French and Dutch boards, scores each one against your CV, and shows you the handful that fit — including the lateral moves you would never have searched for.",
    heroTitle: 'AI took your job. Now make it find your next one.',
    subtitle:
      "Pivot reads your CV, interviews you about where you'd rather be, then scores every job on Belgium's niche boards against your answers. Every day.",
    googleNote:
      'Google shares your name and email address. It cannot see your contacts, your calendar, or your files. It saves you inventing another password.',
    scrollCue: 'See how it works',
    shotDashboard: "Pivot's dashboard, showing today's ranked matches and their scores",
    turnEyebrow: 'An example of a lateral move',
    // Every `turnTo*` is a real job title taken from the corpus Pivot scraped,
    // so the section shows the product's own output rather than invented copy.
    // These three are verbatim from `eurobrussels` and `acodev`. The `turnFrom*`
    // side is the visitor's hypothetical current role, which is why `turnNote`
    // claims only the destination is real — and claims it without saying the
    // offer is open *today*, because the corpus is a rolling 15-day window and
    // these postings will age out.
    //
    // Kept short on purpose: all three share one line, and the line must never
    // wrap (see `.landing-turn-pair`). Roughly 40 characters per pair, total,
    // is the ceiling — check the longest locale before lengthening one.
    turnFrom1: 'Bookkeeper',
    turnTo1: 'Grant Manager',
    turnFrom2: 'Teacher',
    turnTo2: 'EU Projects Officer',
    turnFrom3: 'Journalist',
    turnTo3: 'Advocacy & Research Manager',
    turnNote:
      'Nothing you would have typed into a search box. Each move ends on a real title from the boards Pivot reads.',
    interviewEyebrow: 'Getting started',
    interviewTitle: "It reads your CV, then asks what's missing",
    interviewBody:
      "Upload your CV and Pivot interviews you: what you're good at, what you'd rather stop doing, which languages you work in, where you'd move toward. It can suggest directions you hadn't considered. You review the profile it writes and correct it in plain language.",
    shotInterview: 'The onboarding interview, mid-conversation',
    rankEyebrow: 'Every day',
    rankTitle: 'Every description read in full, scored on three axes',
    rankBody:
      'Claude opens each posting and reads it end to end. Each one gets a score for experience, values, and salary, a short explanation, and a tag: a direct step, or a step sideways.',
    rankHonest:
      'When nothing scores well, the list is short or empty. Pivot never pads it to look busy.',
    shotMatch: 'A handful of matches, each with its three scores and its direct or lateral tag',
    sourcesEyebrow: 'Where it looks',
    sourcesTitle: "The boards you don't have time to visit",
    sourcesBody:
      "Belgium's long tail: the social sector, culture, non-profits, universities, the EU quarter. Fragmented across French and Dutch, and never matched against a profile. Pivot reads all of them every morning.",
    sourcesFrLabel: 'French-language',
    sourcesNlLabel: 'Dutch-language',
    sourcesDeLabel: 'German-language',
    sourcesEnLabel: 'English-language',
    sourcesAside: "LinkedIn and Indeed are not sources. You're already looking there.",
    trustHeading: 'Non-negotiables',
    trustLangs: 'Français · Nederlands · English',
    trustConsent: 'Your CV is read only after you agree',
    trustErasure: 'Delete your account and everything in it, any time',
    trustResidency: 'Stored in the EU',
    closeTitle: 'Dare to change',
    closeBody: 'Sign in, drop your CV, and see what today looks like.',
    footerRights: 'All rights reserved.',
    footerPrivacy: 'Privacy policy',
  },
  nav: {
    dashboard: 'Dashboard',
    saved: 'Saved',
    profile: 'Profile',
  },
  auth: {
    emailLabel: 'Email',
    passwordLabel: 'Password',
    newPasswordLabel: 'New password',
    orDivider: 'or',
    emailSignInCta: 'Sign in with email',
    loginTitle: 'Sign in',
    loginCta: 'Sign in',
    loginNoAccount: "Don't have an account?",
    loginCreateAccount: 'Create one',
    loginForgot: 'Forgot your password?',
    signupTitle: 'Create an account',
    signupCta: 'Create account',
    signupHaveAccount: 'Already have an account?',
    signupSignIn: 'Sign in',
    signupPasswordHint: 'At least 8 characters.',
    forgotTitle: 'Forgot your password',
    forgotBody: "Enter your email — if an account matches it, you'll get a reset link.",
    forgotCta: 'Send reset link',
    forgotSent: 'If that email matches an account, a reset link is on its way.',
    backToLogin: 'Back to sign in',
    resetTitle: 'Set a new password',
    resetBody: 'Choose a new password for your account.',
    resetCta: 'Reset password',
    resetSuccess: 'Your password has been updated. You can sign in now.',
    resetInvalidToken: 'This link is invalid or has expired. Request a new one.',
    genericError: 'Something went wrong. Please try again.',
    sessionExpired: 'Your session ended. Please sign in again.',
    googleUnavailable: 'Google sign-in is not configured for this build. Use email instead.',
  },
  onboarding: {
    pageTitle: 'Get started',
    intentTitle: 'What brings you here?',
    intentBody: 'Pick whatever feels closest — it just helps us ask better questions.',
    intentPivotNewSector: 'Moving into a new sector',
    intentSameSectorNewRole: 'Same sector, a new kind of role',
    intentKnowDirection: 'I know where I want to go',
    intentUnsure: "Not sure yet — I'm exploring",
    consentTitle: 'Before we start',
    consentBody:
      'Pivot can send your CV text, answers, profile, and relevant job text to Anthropic, OpenAI, Voyage AI, OpenRouter, and the inference provider selected by OpenRouter. Read the privacy policy for the full provider list. Pivot encrypts its own copy of your CV at rest. Pivot makes no claim about what the listed providers do with the text. Pivot never shares your data with other users.',
    consentAccept: 'I agree, continue',
    consentDecline: 'Not now',
    consentPolicyLink: 'Read the full privacy policy',
    declinedTitle: 'Nothing has been processed',
    declinedBody:
      'No CV has been read and no answers have been stored. Nothing happens until you agree. Come back whenever you want — or sign out and leave it there.',
    declinedBack: 'Go back and read it again',
    stepIntent: 'Your reason',
    stepConsent: 'Consent',
    stepUpload: 'Your CV',
    stepInterview: 'The conversation',
    stepReview: 'Review',
    stepsInterviewNote:
      'This part is a conversation, so it runs as long as it needs to — usually a handful of questions.',
    backToProfile: 'Back to my profile',
    uploadTitle: 'Upload your CV',
    uploadBody: 'A PDF works best — we pull your background from it to start the conversation.',
    uploadCta: 'Drag a PDF here, or choose a file',
    uploadFormatHint: 'PDF, up to {{limit}} MB',
    uploadInvalidType: 'That file is not a PDF. Please choose a PDF.',
    uploadTooLarge: 'That file is over {{limit}} MB. Choose a smaller PDF and try again.',
    uploadRejectedType:
      "The server could not read that as a PDF — a renamed .docx or an image does it. Export your CV as a real PDF and it'll go through.",
    uploadedAs: 'Received:',
    uploadContinue: 'Continue',
    cvReading: 'Reading your CV. This can take a few minutes. That is normal.',
    cvReadingPatience:
      'Still reading. That is normal: a model reads it properly and does not scan for keywords.',
    savedCvEyebrow: 'Already on file',
    savedCvBody: 'We kept your CV and what we read from it. No need to upload it again.',
    savedCvUploadedOn: 'Uploaded {{date}}',
    savedCvAnalysisLabel: 'What we read from it',
    savedCvUse: 'Continue with this CV',
    savedCvReplace: 'Upload a different CV',
    savedCvKeep: 'Keep the CV I have',
    interviewTitle: 'A few questions',
    interviewIntro: "Let's talk about what you're really looking for.",
    interviewPlaceholder: 'Type your answer…',
    interviewWriteOption: 'Chat about this — write your own answer',
    interviewMultiHint: 'Pick every one that fits. Several answers are allowed here.',
    interviewConfirm: 'Send selection',
    interviewSend: 'Send',
    interviewSendHint: 'Enter to send · Shift + Enter for a new line',
    interviewThinking: 'Preparing the next question',
    interviewThinkingPatience:
      'Still going — each question is chosen from what you have said, not a fixed script.',
    interviewConsentStale:
      'Your consent needs renewing before we can continue this interview. Check your profile.',
    constraintsTitle: 'Where can you actually work?',
    constraintsBody:
      "Short and factual. These are the lines we won't cross when we pick your matches — so if one of them doesn't limit you, say so out loud.",
    constraintsLocationsLabel: 'Places you would work',
    constraintsLocationsHint: 'Tick every province where you could work.',
    constraintsLocationsAnywhere: 'Anywhere is fine',
    provinceBrussels: 'Brussels',
    provinceAntwerp: 'Antwerp',
    provinceLimburg: 'Limburg',
    provinceEastFlanders: 'East Flanders',
    provinceWestFlanders: 'West Flanders',
    provinceFlemishBrabant: 'Flemish Brabant',
    provinceHainaut: 'Hainaut',
    provinceLiege: 'Liège',
    provinceNamur: 'Namur',
    provinceLuxembourg: 'Luxembourg',
    provinceWalloonBrabant: 'Walloon Brabant',
    locationChipRemove: 'Remove {{location}}',
    constraintsAnchorLabel: 'Where you commute from',
    constraintsAnchorHint: 'One place. It sets how far each job actually is.',
    constraintsAnchorNone: 'No fixed base',
    constraintsContractLabel: 'Contracts you would take',
    constraintsContractCdi: 'Permanent',
    constraintsContractCdd: 'Fixed term',
    constraintsContractFreelance: 'Freelance',
    constraintsContractInterim: 'Temp work',
    constraintsContractAny: 'Any contract',
    constraintsSalaryLabel: 'Lowest gross monthly salary you would accept',
    constraintsSalaryHint: 'Euros per month. Nobody sees this but the scoring.',
    constraintsSalaryNoFloor: 'No floor',
    constraintsLanguagesLabel: 'Working languages',
    constraintsLanguagesHint: 'The level decides whether a job that demands it is a match.',
    constraintsLanguagesFormatHint:
      'One per language, with the level in brackets — french (native), english (fluent).',
    constraintsLanguageFrench: 'French',
    constraintsLanguageDutch: 'Dutch',
    constraintsLanguageEnglish: 'English',
    constraintsLevelChoose: 'Choose a level',
    constraintsLevelNone: "I don't speak it",
    constraintsLevelBasic: 'Basic',
    constraintsLevelIntermediate: 'Intermediate',
    constraintsLevelFluent: 'Fluent',
    constraintsLevelNative: 'Native',
    constraintsLanguagesNone: "I'd rather not state my languages",
    constraintsSubmit: 'Build my profile',
    constraintsIncomplete:
      'Answer each question, or tick the box that says it does not limit you. A blank answer would be read as "anywhere".',
    constraintsSaveFailed: "We couldn't save your answers just now. Try again.",
    constraintsOwnEyebrow: 'Your own hard limits',
    baselineEyebrow: 'The job you actually do',
    baselineBody:
      'This is the work you do now, or did last — not the work you are looking for. We use it to tell a career change apart from a step sideways, so it has to be right.',
    baselineRoleLabel: 'Your job',
    baselineDutiesLabel: 'What the job involves',
    baselineSectorsLabel: 'Sectors you have worked in',
    baselineUnknownValue: 'Not known yet',
    baselineStateUnknown:
      'We could not read a job from what you gave us. Tell us what it is, and pick where it comes from.',
    baselineStateUnconfirmed: 'Read from your CV and profile. Confirm it, or correct it.',
    baselineStateConfirmed: 'You confirmed this.',
    baselineStateMissing: 'No job on file yet. Tell us what it is.',
    baselineConfirm: 'Yes, that is my job',
    baselineCorrect: 'No, correct it',
    baselineKeep: 'Leave my job as it is',
    baselineRoleTitleLabel: 'What is the job called',
    baselineSourceLabel: 'Where this comes from',
    baselineSourceHint: 'Pick the entry this job is based on. We only store what you gave us.',
    baselineNoSources: 'There is nothing on file to base a job on yet.',
    baselineMixedCareer: 'My career is mixed — there is no single job',
    baselineListHint: 'Separate each one with a comma.',
    matchSetupTitle: 'Set up your search',
    matchSetupBody:
      "Short and factual, like the questions above. These become the exact rules we search against — so if one doesn't limit you, say so.",
    matchSetupLocationsLabel: 'Places you would work',
    matchSetupLocationsHint: 'Tick every province where you could work.',
    matchSetupLocationsNoPreference: 'Anywhere is fine',
    matchSetupContractLabel: 'Contracts you would take',
    matchSetupContractNoPreference: 'Any contract',
    matchSetupScheduleLabel: 'Working hours',
    matchSetupScheduleHint: 'Tell us what works and what does not — both are useful.',
    matchSetupScheduleAllowedLabel: 'Fine with',
    matchSetupScheduleExcludedLabel: 'Not for me',
    matchSetupScheduleDay: 'Daytime',
    matchSetupScheduleEvening: 'Evenings',
    matchSetupScheduleNight: 'Nights',
    matchSetupScheduleWeekend: 'Weekends',
    matchSetupScheduleRotatingShift: 'Rotating shifts',
    matchSetupScheduleOnCall: 'On call',
    matchSetupScheduleNoPreference: 'Any schedule is fine',
    matchSetupPayLabel: 'Lowest pay you would accept',
    matchSetupPayHint:
      'Give the amount, then the currency, period, and whether it is gross or net.',
    matchSetupPayAmountLabel: 'Amount',
    matchSetupPayCurrencyLabel: 'Currency',
    matchSetupPayPeriodLabel: 'Per',
    matchSetupPayPeriodHour: 'hour',
    matchSetupPayPeriodDay: 'day',
    matchSetupPayPeriodMonth: 'month',
    matchSetupPayPeriodYear: 'year',
    matchSetupPayPeriodUnknown: 'not sure',
    matchSetupPayGrossOrNetLabel: 'Gross or net',
    matchSetupPayGross: 'Gross',
    matchSetupPayNet: 'Net',
    matchSetupPayGrossOrNetUnknown: 'Not sure',
    matchSetupPayNoPreference: 'No floor',
    matchSetupExclusionsLabel: 'Work you will not do',
    matchSetupExclusionsHint: 'One per line — cold calling, night shifts, whatever it is for you.',
    matchSetupEmployerLabel: 'Employers you will not work for',
    matchSetupEmployerHint:
      'Tick only what you mean. Refusing large groups is not the same as refusing every company.',
    matchSetupEmployerAllCommercial: 'Any commercial company',
    matchSetupEmployerLargeCorporate: 'Large corporate groups',
    matchSetupEmployerStrengthLabel: 'How strong is this?',
    matchSetupEmployerStrengthHard: 'Never show me these jobs',
    matchSetupEmployerStrengthSoft: 'Show them lower down',
    matchSetupLanguagesLabel: 'Working languages',
    matchSetupLanguagesHint: 'The level decides whether a job that demands it is a match.',
    matchSetupSubmit: 'Save my search setup',
    matchSetupIncomplete:
      'Answer each question, or tick the box that says it does not limit you. A blank answer would be read as "anywhere".',
    matchSetupSaveFailed: "We couldn't save your answers just now. Try again.",
    reviewTitle: 'Your profile',
    reviewBody: 'Review and edit before continuing — this drives every future match.',
    reviewSourceEyebrow: 'From your CV and interview',
    reviewSummaryLabel: 'Summary',
    reviewSkillsLabel: 'Skills',
    reviewWantsLabel: "What you're after",
    reviewIntentLabel: 'What brought you here',
    reviewFinish: 'See my first matches',
    profileBuilding: 'Building your profile',
    profileBuildingPatience:
      'Still building \u2014 this is the profile every future match gets scored against.',
    finalizeFailed: "We couldn't build your profile just now. Your answers are safe — try again.",
    saveFailed: "We couldn't save your profile just now. Try again.",
    entryLoadFailed: "We couldn't tell where you left off. Try again.",
    routeAfterInterviewFailed: "We couldn't tell what comes next. Try again.",
    retry: 'Try again',
    policyReviewTitle: 'Approve your search',
    policyReviewBody: 'This is the exact search we will run. Check it, then approve it.',
    policyReviewLocationsLabel: 'Places',
    policyReviewContractLabel: 'Contracts',
    policyReviewScheduleLabel: 'Hours',
    policyReviewPayLabel: 'Pay floor',
    policyReviewExclusionsLabel: 'Excluded work',
    policyReviewDesiredWorkLabel: 'Desired work',
    policyReviewUnwantedWorkLabel: 'Unwanted work',
    policyReviewNoPreference: 'No preference stated',
    policyReviewBlocked: 'Needs an answer: {{reason}}',
    policyReviewBlockerGeneric: 'this answer',
    policyReviewEmployerLabel: 'Employers you will not work for',
    policyReviewEmployerAllCommercialHard:
      'Never show me a job at a commercial company.',
    policyReviewEmployerAllCommercialSoft:
      'Put jobs at commercial companies lower down my list.',
    policyReviewEmployerLargeCorporateHard:
      'Never show me a job at a large corporate group.',
    policyReviewEmployerLargeCorporateSoft:
      'Put jobs at large corporate groups lower down my list.',
    policyReviewEmployerTypesHard:
      'Never show me a job at these kinds of employer: {{types}}.',
    policyReviewEmployerTypesSoft:
      'Put jobs at these kinds of employer lower down my list: {{types}}.',
    policyReviewOrganizationPublicService: 'public service',
    policyReviewOrganizationNonprofit: 'non-profit',
    policyReviewOrganizationAcademic: 'university or school',
    policyReviewOrganizationCommercial: 'commercial company',
    policyReviewOrganizationLargeCommercial: 'large corporate group',
    policyReviewEmployerOtherScope: 'Employers of this kind: {{scope}}.',
    policyReviewEmployerApprove: 'Approve this wording',
    policyReviewEmployerApproved: 'Approved',
    policyReviewEmployerApproveFailed:
      'That wording could not be approved. Reload this page and try again.',
    policyReviewApprove: 'Approve and continue',
    policyReviewLoadFailed: "We couldn't load your search. Try again.",
    policyReviewChangedElsewhere:
      'Your search changed elsewhere — in another tab, or since you opened this page. Reload to see the current one.',
    policyReviewApproveFailed: "We couldn't approve your search just now. Try again.",
    policyReviewReload: 'Reload',
    upgradeEvidenceTitle: 'Review your experience',
    upgradeEvidenceBody:
      'Redo the interview to build the evidence your job search needs. Your saved profile stays as it is until you finish and save again. The new search starts after you approve it.',
    upgradeEvidenceAction: 'Redo the interview',
    scoringTitle: 'Finding your first matches',
    scoringBody: 'Reading live offers against your profile. This takes a moment — worth it.',
    scoringPatience:
      'Still going \u2014 every offer is read in full, which is why this is not instant.',
    scoringStillGoing: "We're still scoring some offers; they'll appear as they're ready.",
  },
  dashboard: {
    title: "Today's matches",
    matchCount: '{{count}} matches today',
    directTitle: 'In line with your search',
    lateralTitle: 'Sideways moves',
    interested: 'Interested',
    notInterested: 'Not interested',
    interestedMarked: 'Marked interested',
    notInterestedMarked: 'Marked not interested',
    dryDayTitle: 'Nothing strong enough today',
    dryDayBody: "We'd rather tell you honestly than pad the list. Check back tomorrow.",
    refineLink: 'Something off about these? Refine your criteria',
    preparingMatches: 'Preparing your first matches',
    preparingMatchesPatience:
      'Still going — every offer is read in full, which is why this is not instant.',
    loadingPatience:
      "Still loading — today's read compares the jobs we reached against your profile, which takes a minute.",
    scoreExperience: 'Experience',
    scoreValues: 'Values',
    scoreSalary: 'Salary',
    scoreNotDisclosed: 'not disclosed',
    tagDirect: 'Direct',
    tagLateral: 'Lateral',
    locationNotStated: 'Place of work not stated',
    viewPosting: 'View posting',
    alsoOn: 'Also on {{sources}}',
    showFullReasoning: 'Show full reasoning',
    analysisLanguageNote: 'This explanation is in {{language}}.',
    analysisLanguageFr: 'French',
    analysisLanguageNl: 'Dutch',
    analysisLanguageEn: 'English',
    reportGone: 'This job no longer exists',
    reportGoneSent: 'Reported',
  },
  matcherV2: {
    recommendedTitle: 'Proved fits',
    shelfTitle: 'Worth a second look',
    shelfBody: 'These need one answer from you before we can judge them either way.',
    tierStrong: 'Strong fit',
    tierPossible: 'Possible fit',
    tierStretch: 'Stretch fit',
    tierUnresolved: 'Needs your input',
    concernsHeading: 'Worth knowing',
    notesHeading: 'Unverified',
    quotesHeading: 'From the posting',
    quoteSourceNote: 'Quoted exactly as posted, in its original language.',
    wouldApplyQuestion: 'Would you apply for this job?',
    wouldApplyYes: "Yes, I'd apply",
    wouldApplyYesMarked: 'Marked: would apply',
    wouldApplyNo: 'No',
    wouldApplyNoMarked: 'Marked: would not apply',
    wouldApplyNoNote: "Noted. This won't change any other match.",
    checkQuestion: 'Had you considered this type of work before?',
    checkYes: 'Yes',
    checkYesMarked: 'Marked: yes, considered before',
    checkNo: 'No',
    checkNoMarked: "Marked: no, hadn't considered it",
    checkUnsure: 'Not sure',
    checkUnsureMarked: 'Marked: not sure',
    missingFactHeading: 'One thing we need from you',
    missingFactQuestion: 'Can you do this?',
    missingFactYes: 'Yes, I can',
    missingFactYesMarked: 'Marked: you can',
    missingFactNo: "No, I can't",
    missingFactNoMarked: "Marked: you can't",
    missingFactUnsure: 'Not sure',
    missingFactUnsureMarked: 'Marked: not sure',
    missingFactUnsureNote: 'Left open. We recorded nothing about you.',
    missingFactReassessing: "Thanks. We're checking this posting again with your answer.",
    insufficientHeading: 'We could not read this posting',
    insufficientBody:
      'There was not enough in the text to judge this job against your profile. Read it yourself and decide.',
    insufficientLink: 'Open the posting',
    staleNote: "This job's information changed. Refreshing…",
    dryDayTitle: 'Nothing proved today',
    dryDayBody:
      "We finished today's reading and found nothing worth recommending. Check back tomorrow.",
    pendingTitle: 'Still checking',
    pendingBody: "We're still verifying today's matches against your profile.",
    morePending: 'More matches are still being checked.',
    reasonDemandMet: 'You meet this requirement.',
    reasonDemandMetUnresolved: 'This requirement is likely met, but not confirmed by your profile.',
    reasonDemandGap: 'This requirement is not met.',
    reasonCoreDemandGapVerified: 'A core requirement is confirmed unmet.',
    reasonCoreDemandGapUnresolved: 'A core requirement is not confirmed either way.',
    reasonHardFailureVerified: 'A hard requirement is confirmed unmet.',
    reasonHardFailureUnresolved: 'A hard requirement is not confirmed either way.',
    reasonRuleConfirmed: 'Confirmed against your stated preferences.',
    reasonSoftRuleContradicted: 'This runs against one of your stated preferences.',
    reasonUnverifiedFact: "The posting doesn't state this clearly.",
    reasonUnknownRuleFact: 'One preference could not be checked.',
    reasonHardLocationConflict: 'The location conflicts with your requirements.',
    reasonHardContractConflict: 'The contract type conflicts with your requirements.',
    reasonHardWorkExclusionConflict: 'This work is on your exclusion list.',
    reasonRequiredLanguageKnownLow: 'A required language is below your stated level.',
    reasonUnrecognized: "A new kind of note we can't yet describe.",
    applyLink: 'Open the posting to apply',
    employerUnverified: 'Employer not stated in the posting',
    recruiterLine: 'Advertised by {{recruiter}}',
    contractLine: 'Contract: {{contract}}',
    careerHeading: 'Career move',
    careerDirect: 'Same work, same kind of employer',
    careerSectorPivot: 'Same work, a different kind of employer',
    careerOccupationPivot: 'Different work, same kind of employer',
    careerOccupationAndSectorPivot: 'Different work, a different kind of employer',
    careerFamiliarOccupation: 'Work you have already done',
    careerUnresolved: 'We have not established how this compares to your work history',
    careerSectorUnverified: "The employer's sector is not stated, so this comparison is partial.",
    explanationHeading: 'Why this job is here',
    transferLine: 'Your {{fact}} supports this job\u2019s {{demand}}.',
    blockingGap: '{{demand}} remains unverified.',
    nonblockingGap: '{{demand}} is not evidenced. It does not block this recommendation.',
    budgetDelayTitle: "Today's checks are paused",
    budgetDelayBody:
      "Today's reading budget is spent, so the rest of your jobs wait for tomorrow. Nothing was lost.",
    policyHoldTitle: 'Waiting on your new rule',
    policyHoldBody:
      'You approved a new employer rule. Matches decided before it are held back until they are checked against it.',
    insufficientStateTitle: 'We could not read today\u2019s postings',
    insufficientStateBody:
      'The postings we reached did not say enough to judge them. Open them yourself and decide.',
  },
  savedSeen: {
    title: 'Saved',
    emptyTitle: 'Nothing saved yet',
    emptyBody:
      "Mark a match as interested on today's dashboard and it lands here, along with anything you apply to.",
    sectionSaved: 'Saved matches',
    sectionDismissed: 'Dismissed matches',
    sectionNoLongerCurrent: 'No longer current',
    sectionNoLongerCurrentBody:
      'The posting is gone, or this job no longer matches your rules. Your notes stay; the recommendation does not.',
    staleReasonDelisted: 'The posting was taken down.',
    staleReasonIneligible: 'Your rules no longer recommend this job.',
    applicationStatusLabel: 'Application status',
    statusNone: 'Not applied',
    statusApplied: 'Applied',
    statusResponded: 'Heard back',
    statusInterview: 'Interview',
    statusOffer: 'Offer',
    outcomePrompt: 'Any word back on this one?',
    outcomeYes: 'Yes',
    outcomeNotYet: 'Not yet',
  },
  apply: {
    tailorCta: 'Tailor CV',
    tailoring: 'Drafting a tailored CV…',
    tailoringPatience: 'Still drafting — checking every line against your CV takes a moment.',
    letterCta: 'Write cover letter',
    writingLetter: 'Drafting a cover letter…',
    writingLetterPatience: 'Still drafting — matching your letter to this job takes a moment.',
    errorConsent: 'Your consent needs renewing before we can use your CV. Check your profile.',
    errorNoCv: 'Upload a CV in your profile before tailoring one for a job.',
    errorNoCvLetter: 'Upload a CV in your profile before writing a cover letter for a job.',
    errorBudget: "You've used today's tailoring budget. Try again tomorrow.",
    reviewTitleCv: 'Review your tailored CV',
    reviewTitleLetter: 'Review your cover letter',
    reviewHint: 'Edit anything before saving — nothing is kept until you save it.',
    save: 'Save',
    download: 'Download as Markdown',
    backToDashboard: 'Back to dashboard',
    noDraft: "There's no draft to review. Start one from a match on your dashboard first.",
  },
  profile: {
    title: 'My profile',
    formTitle: 'Your details',
    formBody: 'Edit what Pivot has on file for you.',
    saveChanges: 'Save changes',
    saved: 'Saved.',
    saveFailed: "We couldn't save your changes just now. Nothing was changed — try again.",
    redoInterview: 'Redo the interview',
    reviewMatcherV2: 'Review my job search',
    redoConfirmBody:
      'This restarts onboarding from the beginning — your reason for looking, consent, CV and the interview itself. Your saved profile stays as it is until you finish and save again.',
    redoConfirmGo: 'Restart onboarding',
    redoFailed: "We couldn't restart onboarding. Try again.",
    rubricTitle: 'How your matches are scored',
    rubricBody: 'Written from your CV and your interview. Change any of it in Refine.',
    rubricVersion: 'Revision {{version}}',
    rubricMissing: 'Finish the interview and this is where your scoring brief will appear.',
    rubricNotStated: 'You have not told us this yet.',
    rubricSummary: 'What a good match looks like',
    rubricMustHaves: 'A job must have',
    rubricDealBreakers: 'A job is ruled out by',
    rubricStrengths: 'What you bring',
    rubricNoEvidence: 'No proof on file yet',
    rubricProvenUnwanted: 'Proven, and not the job you want',
    rubricProvenUnwantedHelp:
      'These count as proof you can handle a role. A job made mostly of them scores low.',
    rubricCommute: 'Where you will work',
    rubricCommuteFrom: 'From',
    rubricCommuteWithin: 'Within {{minutes}} min',
    rubricCommuteBy: 'By',
    rubricCommuteRegions: 'Regions',
    rubricCommuteExamples: 'For example',
    rubricAxesHeading: 'What earns the score',
    rubricAxisExperience: 'Experience score',
    rubricAxisValues: 'Values score',
    rubricAxisSalary: 'Salary score',
    rubricShare: '{{share}}%',
    rubricLateral: 'Sideways moves worth showing you',
    rubricTechnicalDepth: 'How technical you are',
    refineTitle: 'Refine',
    refineBody:
      'Tell the adviser what the scoring above gets wrong. It has read your brief and the jobs you marked interested or not interested.',
    refinePlaceholder: 'E.g. "these are still too much shop-floor work"…',
    refineSend: 'Send',
    refining: 'Reading your brief and your matches',
    refiningPatience: 'Still going — it is rewriting the brief, not just replying.',
    refineYou: 'You',
    refineAdviser: 'Adviser',
    refineNoChange: 'Nothing changed yet — answer above and it will.',
    refineApplied: 'Applied from your next matches on.',
    refineUndo: 'Undo',
    refineUndone: 'Undone',
    refineOpener:
      "What is the scoring getting wrong? A job it rated well that you'd never take is the most useful thing you can tell me.",
    exportTitle: 'Download my data',
    exportBody:
      'Everything Pivot holds about you, as one JSON file: your profile, your scores, your consent and notification history, and the CV analysis (not the file itself). Useful before you delete your account, or any time you want to see exactly what is stored.',
    exportButton: 'Download my data',
    exportFailed: "We couldn't prepare your export just now. Try again.",
    signOutEverywhereTitle: 'Sign out everywhere',
    signOutEverywhereBody:
      'Ends every signed-in session on your account, including this one. Anyone else with your password is signed out too. Sign back in whenever you like — nothing else changes.',
    signOutEverywhereButton: 'Sign out everywhere',
    signOutEverywhereFailed:
      "We couldn't sign out your other sessions just now. You're still signed in here — try again.",
    changePasswordTitle: 'Change password',
    changePasswordBody:
      'We email you a reset link instead of asking for a new password here. The link works once, for 30 minutes, and setting the new password signs out every other session.',
    changePasswordButton: 'Email me a reset link',
    changePasswordSent: 'We sent a reset link to {{email}}. It works once, for 30 minutes.',
    changePasswordFailed: "We couldn't send the reset link just now. Try again.",
    deleteTitle: 'Delete my account',
    deleteBody:
      'This erases your account, your profile, your scores, your consent record and your encrypted CV file, in one operation. It is immediate and permanent — there is no grace period and nothing is recoverable.',
    deleteStart: 'Delete my account',
    deleteConfirmLabel: 'I understand this is permanent and cannot be undone.',
    deleteConfirm: 'Erase everything now',
    deleteFailed: "We couldn't delete your account just now. Nothing was removed — try again.",
    privacyLink: 'What we hold, and why — the privacy policy',
    accountSectionLabel: 'Your account',
  },
  privacy: {
    title: 'Privacy policy',
    version: 'Policy version {{version}} · last updated {{updated}}',
    intro:
      'This is plain language, not a legal contract. It describes what Pivot actually does with your data.',
    whatTitle: 'What Pivot is',
    whatBody:
      'A job-matching web app. You give it your CV and answer a short interview; it matches you against job offers scraped from Belgian job boards and ranks them with configured AI models.',
    collectTitle: 'What we collect, and why',
    collectCv:
      'Your CV (PDF) — to build your profile. Encrypted, in Google Cloud Storage. The database stores only a reference to it, never the file.',
    collectProfile:
      'Your structured profile (skills, experience, preferences) — the thing job offers get matched against.',
    collectAnswers:
      'Your onboarding answers — folded into that profile. The raw transcript exists only while your onboarding is in progress: it is deleted the moment you finish, or after 30 days if you never do.',
    collectAccount: 'Your email and account id — so we know it is you when you sign in.',
    collectInterest:
      'Which jobs you marked interested or not interested — to tune your future matches.',
    collectConsent:
      'Your consent record (policy version and timestamp) — proof you agreed before we touched your CV.',
    collectNever:
      'We do not collect payment details, do not sell or share your data with advertisers, and do not use your data to train any AI model.',
    anthropicTitle: 'AI providers',
    anthropicBody:
      'Pivot can send your CV text, answers, structured profile, profile-derived search text, relevant job text, and related prompts and outputs to Anthropic, OpenAI, Voyage AI, or OpenRouter. OpenRouter can route the request to one of the inference providers named in this policy. Pivot has no data-processing agreement with these AI providers yet.',
    residencyTitle: 'Where your data lives',
    residencyBody:
      'The database, the CV storage bucket and the app itself all run in Google Cloud europe-west1 (Belgium).',
    storageTitle: 'Cookies and local storage',
    storageBody:
      'Pivot sets no cookies of its own, and runs no analytics, advertising, or third-party tracking. Two items are kept in your browser, both strictly necessary for the app to work:',
    storageSession:
      'Your sign-in token, so you stay signed in between visits. Removed when you sign out.',
    storageLocale: 'Which language you picked — French, Dutch, or English.',
    storageGoogle:
      'The "Sign in with Google" button runs a script served by accounts.google.com. That script is Google\'s, and Google may read or set its own cookies under google.com while it runs. It loads as soon as a page showing that button opens, rather than only when you click it. Signing in with email avoids it entirely.',
    storageControl:
      'Clearing your browser data removes all of it. None of it profiles you, and none of it is shared with anyone.',
    consentTitle: 'Consent',
    consentBody:
      'Before Pivot reads your CV or sends candidate data to an AI provider, you are asked to consent explicitly. Current test and friend accounts do not renew consent for this development update, and Pivot will delete the complete development dataset. A new provider policy and explicit consent are required before one user who is not a friend joins.',
    erasureTitle: 'Your right to erasure',
    erasureBody:
      'You can delete your account and everything tied to it at any time, from the "Delete my account" button on your profile page. It is not a soft delete: your user row, profile, scores, consent records and notification history go from the database, and your encrypted CV file goes from object storage, in the same operation. Nothing is recoverable afterwards — export anything you want to keep first.',
    minimizationTitle: 'Data minimization',
    minimizationBody:
      'We store only what matching needs. The raw interview transcript exists only while your onboarding is in progress — deleted the moment you finish, or after 30 days if you never do — and job offers are kept only while they are actually live.',
    contactTitle: 'Who to contact',
    contactBody:
      'Until Pivot has a dedicated privacy contact, reach the team through the channel you signed up via.',
    statusTitle: 'Status of this policy',
    statusBody:
      'This is a development policy for test users and friends. Legal review has not happened, and no data-processing agreement is in place for the approved AI providers. Both are required before one user who is not a friend joins.',
    back: 'Back',
  },
  a11y: {
    matchScores: 'Match scores',
    scoreBand: '{{band}} out of {{max}}',
    languageSwitch: 'Language',
    logo: 'Pivot logo',
    onboardingSteps: 'Onboarding steps',
    interviewAnswer: 'Your answer',
    interviewMultiOptions: 'Answer options — choose one or more',
    themeToggleToDark: 'Switch to dark mode',
    themeToggleToLight: 'Switch to light mode',
    applyReviewTextarea: 'Tailored CV content',
    skipToMain: 'Skip to main content',
    authHomeLink: 'Pivot — go to the homepage',
  },
}

export default en
